// Month-grid calendar tinted by per-day occurrence count. Ported from
// MedicalAssistant CountCalendar.tsx, minus the medical-specific overlay /
// intensity layers. Month arithmetic is civil YYYY-MM (no local Date) so a
// DST transition cannot skip or duplicate a month.

import { useMemo, useState } from "react";
import { localCalendarDate, pad2 } from "@lifelog/shared";

const DOW_LABELS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

function shiftMonth(yearMonth: string, delta: number): string {
  const [y, m] = yearMonth.split("-").map(Number);
  const idx = (y ?? 0) * 12 + ((m ?? 1) - 1) + delta;
  const ny = Math.floor(idx / 12);
  const nm = ((idx % 12) + 12) % 12;
  return `${ny}-${pad2(nm + 1)}`;
}

export function CountCalendar({
  counts,
  timezone,
  selectedDay,
  onDayClick,
}: {
  counts: Map<string, number>;
  timezone: string;
  selectedDay?: string | null;
  onDayClick?: (date: string) => void;
}) {
  const today = localCalendarDate(new Date().toISOString(), timezone);
  const [yearMonth, setYearMonth] = useState<string>(today.slice(0, 7));
  const [year, month] = yearMonth.split("-").map(Number) as [number, number];

  const monthCounts = useMemo(() => {
    const m = new Map<string, number>();
    for (const [k, v] of counts) {
      if (k.startsWith(yearMonth)) m.set(k, v);
    }
    return m;
  }, [counts, yearMonth]);

  const max = useMemo(() => Math.max(1, ...monthCounts.values(), 0), [monthCounts]);

  const monthLabel = new Date(Date.UTC(year, month - 1, 1)).toLocaleDateString(undefined, {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });

  const firstDow = new Date(Date.UTC(year, month - 1, 1)).getUTCDay();
  const daysInMonth = new Date(Date.UTC(year, month, 0)).getUTCDate();
  const cells: (number | null)[] = [
    ...Array.from({ length: firstDow }, () => null),
    ...Array.from({ length: daysInMonth }, (_, i) => i + 1),
  ];
  while (cells.length % 7 !== 0) cells.push(null);

  return (
    <div className="select-none">
      <div className="mb-3 flex items-center justify-between">
        <button
          type="button"
          onClick={() => setYearMonth(shiftMonth(yearMonth, -1))}
          className="flex min-h-[44px] min-w-[44px] items-center justify-center rounded-md text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 sm:min-h-0 sm:min-w-0 sm:p-1.5"
          aria-label="Previous month"
        >
          &#8249;
        </button>
        <span className="text-sm font-semibold text-gray-700 dark:text-gray-200">{monthLabel}</span>
        <button
          type="button"
          onClick={() => setYearMonth(shiftMonth(yearMonth, 1))}
          disabled={yearMonth >= today.slice(0, 7)}
          className="flex min-h-[44px] min-w-[44px] items-center justify-center rounded-md text-gray-500 hover:bg-gray-100 disabled:opacity-30 dark:hover:bg-gray-700 sm:min-h-0 sm:min-w-0 sm:p-1.5"
          aria-label="Next month"
        >
          &#8250;
        </button>
      </div>

      <div className="mb-1 grid grid-cols-7 gap-px text-center">
        {DOW_LABELS.map((d) => (
          <div key={d} className="text-[10px] font-medium text-gray-400">
            {d}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-px">
        {cells.map((day, idx) => {
          if (day === null) return <div key={`pad-${idx}`} />;
          const dateStr = `${yearMonth}-${pad2(day)}`;
          const count = monthCounts.get(dateStr) ?? 0;
          const isToday = dateStr === today;
          const isFuture = dateStr > today;
          const isSelected = selectedDay === dateStr;
          const clickable = count > 0 && !isFuture;
          const tint = count > 0 ? Math.max(0.18, count / max) : 0;
          return (
            <button
              key={dateStr}
              type="button"
              onClick={() => clickable && onDayClick?.(dateStr)}
              disabled={!clickable}
              className={[
                "relative flex min-h-[44px] flex-col items-center justify-center gap-0.5 rounded-md p-1 text-xs",
                isToday ? "ring-2 ring-stone-400 dark:ring-stone-500" : "",
                isSelected ? "ring-2 ring-(--accent)" : "",
                isFuture
                  ? "opacity-30"
                  : clickable
                    ? "cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                    : "cursor-default",
              ].join(" ")}
              style={
                count > 0
                  ? {
                      backgroundColor: `color-mix(in srgb, var(--accent) ${Math.round(tint * 55)}%, transparent)`,
                    }
                  : undefined
              }
              aria-label={`${dateStr}: ${count} occurrence${count !== 1 ? "s" : ""}`}
            >
              <span
                className={
                  "text-xs font-medium " +
                  (isToday ? "text-stone-700 dark:text-stone-300" : "text-gray-600 dark:text-gray-400")
                }
              >
                {day}
              </span>
              {count > 0 && (
                <span className="text-[11px] font-semibold text-gray-800 dark:text-gray-100" aria-hidden="true">
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
